// API 클라이언트 유틸리티
export interface ApiResponse<T = any> {
  success: boolean;
  message: string;
  timestamp: string;
  data?: T;
}

export interface ErrorResponse {
  error: string;
  message: string;
  details?: any;
  timestamp: string;
  path: string;
  statusCode: number;
}

export class ApiError extends Error {
  public statusCode: number;
  public error: string;
  public details?: any;
  public path: string;
  public timestamp: string;

  constructor(response: ErrorResponse) {
    super(response.message);
    this.statusCode = response.statusCode;
    this.error = response.error;
    this.details = response.details;
    this.path = response.path;
    this.timestamp = response.timestamp;
    this.name = 'ApiError';
  }
}

class ApiClient {
  private baseURL: string;
  private defaultHeaders: Record<string, string>;

  constructor() {
    this.baseURL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
    this.defaultHeaders = {
      'Content-Type': 'application/json',
    };
  }

  private getAuthHeaders(): Record<string, string> {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    const contentType = response.headers.get('content-type');
    const isJson = contentType?.includes('application/json');
    
    if (!isJson) {
      // 서버가 실행되지 않거나 HTML 에러 페이지를 반환하는 경우
      if (response.status === 0 || response.status >= 500) {
        throw new Error('서버에 연결할 수 없습니다. 백엔드 서버가 실행 중인지 확인해주세요.');
      }
      throw new Error(`응답 형식이 올바르지 않습니다. (상태 코드: ${response.status})`);
    }

    try {
      const data = await response.json();

      if (!response.ok) {
        // API 에러 응답
        if (data && typeof data === 'object' && 'error' in data) {
          throw new ApiError(data as ErrorResponse);
        }
        // 일반적인 HTTP 에러
        throw new Error(`서버 오류가 발생했습니다. (상태 코드: ${response.status})`);
      }

      return data;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      if (error instanceof Error) {
        throw new Error(`응답을 처리하는 중 오류가 발생했습니다: ${error.message}`);
      }
      throw new Error('알 수 없는 오류가 발생했습니다.');
    }
  }

  async get<T = any>(endpoint: string, params?: Record<string, any>): Promise<T> {
    try {
      const url = new URL(`${this.baseURL}${endpoint}`);
      
      if (params) {
        Object.keys(params).forEach(key => {
          if (params[key] !== undefined && params[key] !== null) {
            url.searchParams.append(key, String(params[key]));
          }
        });
      }

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          ...this.defaultHeaders,
          ...this.getAuthHeaders(),
        },
      });

      return this.handleResponse<T>(response);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      if (error instanceof Error) {
        throw new Error(`API 요청 중 오류가 발생했습니다: ${error.message}`);
      }
      throw new Error('네트워크 오류가 발생했습니다.');
    }
  }

  async post<T = any>(endpoint: string, body?: any): Promise<T> {
    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        method: 'POST',
        headers: {
          ...this.defaultHeaders,
          ...this.getAuthHeaders(),
        },
        body: body ? JSON.stringify(body) : undefined,
      });

      return this.handleResponse<T>(response);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      if (error instanceof Error) {
        throw new Error(`API 요청 중 오류가 발생했습니다: ${error.message}`);
      }
      throw new Error('네트워크 오류가 발생했습니다.');
    }
  }

  async put<T = any>(endpoint: string, body?: any): Promise<T> {
    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        method: 'PUT',
        headers: {
          ...this.defaultHeaders,
          ...this.getAuthHeaders(),
        },
        body: body ? JSON.stringify(body) : undefined,
      });

      return this.handleResponse<T>(response);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      if (error instanceof Error) {
        throw new Error(`API 요청 중 오류가 발생했습니다: ${error.message}`);
      }
      throw new Error('네트워크 오류가 발생했습니다.');
    }
  }

  async delete<T = any>(endpoint: string): Promise<T> {
    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        method: 'DELETE',
        headers: {
          ...this.defaultHeaders,
          ...this.getAuthHeaders(),
        },
      });

      return this.handleResponse<T>(response);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      if (error instanceof Error) {
        throw new Error(`API 요청 중 오류가 발생했습니다: ${error.message}`);
      }
      throw new Error('네트워크 오류가 발생했습니다.');
    }
  }

  async patch<T = any>(endpoint: string, body?: any): Promise<T> {
    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        method: 'PATCH',
        headers: {
          ...this.defaultHeaders,
          ...this.getAuthHeaders(),
        },
        body: body ? JSON.stringify(body) : undefined,
      });

      return this.handleResponse<T>(response);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      if (error instanceof Error) {
        throw new Error(`API 요청 중 오류가 발생했습니다: ${error.message}`);
      }
      throw new Error('네트워크 오류가 발생했습니다.');
    }
  }

  // 파일명을 안전하게 처리하는 함수
  private sanitizeFileName(fileName: string): string {
    // 한글 파일명을 안전하게 처리
    const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'));
    const extension = fileName.substring(fileName.lastIndexOf('.'));
    
    // 특수문자 제거 및 공백을 언더스코어로 변경
    const sanitizedName = nameWithoutExt
      .replace(/[^\w\s가-힣]/g, '') // 한글, 영문, 숫자, 공백만 허용
      .replace(/\s+/g, '_') // 공백을 언더스코어로 변경
      .replace(/_+/g, '_') // 연속된 언더스코어를 하나로
      .trim();
    
    // 파일명이 비어있으면 기본값 사용
    const finalName = sanitizedName || 'file';
    
    return `${finalName}${extension}`;
  }

  // 파일 업로드를 위한 메서드
  async uploadFile<T = any>(endpoint: string, file: File, additionalData?: Record<string, any>): Promise<T> {
    try {
      const formData = new FormData();
      
      // 파일명을 안전하게 처리
      const sanitizedFileName = this.sanitizeFileName(file.name);
      const sanitizedFile = new File([file], sanitizedFileName, {
        type: file.type,
        lastModified: file.lastModified,
      });
      
      formData.append('image', sanitizedFile);
      
      if (additionalData) {
        Object.keys(additionalData).forEach(key => {
          formData.append(key, String(additionalData[key]));
        });
      }

      const response = await fetch(`${this.baseURL}${endpoint}`, {
        method: 'POST',
        headers: {
          ...this.getAuthHeaders(),
        },
        body: formData,
      });

      return this.handleResponse<T>(response);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      if (error instanceof Error) {
        throw new Error(`파일 업로드 중 오류가 발생했습니다: ${error.message}`);
      }
      throw new Error('네트워크 오류가 발생했습니다.');
    }
  }

  // 파일 다운로드를 위한 메서드
  async downloadFile(endpoint: string, filename?: string): Promise<Blob> {
    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        method: 'GET',
        headers: {
          ...this.getAuthHeaders(),
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new ApiError({
          error: 'DOWNLOAD_ERROR',
          message: errorData.message || '파일 다운로드에 실패했습니다.',
          details: errorData,
          timestamp: new Date().toISOString(),
          path: endpoint,
          statusCode: response.status,
        });
      }

      return await response.blob();
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      if (error instanceof Error) {
        throw new Error(`파일 다운로드 중 오류가 발생했습니다: ${error.message}`);
      }
      throw new Error('네트워크 오류가 발생했습니다.');
    }
  }
}

export const apiClient = new ApiClient();
export default apiClient;